/**
 * InlineGroupCreator
 *
 * Collapsed "New group" link that expands into a mini-form for creating
 * a workspace-scoped group on the fly. Used inside group-picker modals so
 * users never have to leave their current flow.
 */

import { useState } from 'react';
import { Plus, Loader2, X } from 'lucide-react';
import { getSupabaseClient } from '../../../lib/supabaseClient';

export interface CreatedGroup {
  id: string;
  name: string;
  displayName: string;
}

interface InlineGroupCreatorProps {
  /** Current workspace ID – required to scope the new group */
  workspaceId: string | null;
  /** Called with the newly-created group so the parent can add it to its list + auto-select it */
  onCreated: (group: CreatedGroup) => void;
}

export function InlineGroupCreator({ workspaceId, onCreated }: InlineGroupCreatorProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => { setOpen(false); setName(''); setError(''); };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setError('Group name is required.'); return; }
    if (!workspaceId) { setError('No workspace selected — please switch to a workspace first.'); return; }

    setSaving(true);
    setError('');
    try {
      const supabase = getSupabaseClient();
      const id = crypto.randomUUID();
      const slug = trimmed.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const { error: dbError } = await supabase.from('groups').insert({
        id,
        name: slug || trimmed,
        display_name: trimmed,
        workspace_id: workspaceId,
        is_system: false,
      });
      if (dbError) throw dbError;
      onCreated({ id, name: slug || trimmed, displayName: trimmed });
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create group');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] mt-1.5"
      >
        <Plus className="h-3.5 w-3.5" />
        Create a new group
      </button>
    );
  }

  return (
    <div className="mt-2 p-3 border border-[var(--slate)] bg-[var(--slate-dim)] space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-[var(--text-sec)]">New group</span>
        <button type="button" onClick={reset} className="text-[var(--text-sec)] hover:text-[var(--text-pri)]">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <input
        autoFocus
        type="text"
        value={name}
        onChange={e => { setName(e.target.value); setError(''); }}
        onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') reset(); }}
        placeholder="Group display name"
        className="w-full px-2.5 py-1.5 text-sm border border-[var(--slate)] focus:outline-none focus:ring-2 focus:ring-blue-400 bg-[var(--bg-raised)]"
      />
      {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !name.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
          Create & select
        </button>
        <button
          type="button"
          onClick={reset}
          className="px-3 py-1.5 text-xs text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
